import { useState } from "react";
import { apiService } from "../services/api.service";
import { toast } from "sonner";
import { Lock, Ticket, Loader2, Shield } from "lucide-react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

interface InvitationGateProps {
    onValidated: (codeInfo: { code: string; codeId: string }) => void;
}

export function InvitationGate({ onValidated }: InvitationGateProps) {
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);

    const handleValidate = async () => {
        if (!code.trim()) {
            toast.error('Please enter an invitation code');
            return;
        }

        setLoading(true);

        try {
            const response = await apiService.validateInvitation(code);
            if (response.valid) {
                onValidated({ code, codeId: response.codeId });
                await apiService.storeInvitationCode({ invitationCodeId: response.codeId });
            }
        } catch (error) {
            toast.error((error as Error).message || 'Invalid invitation code');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="w-full max-w-md mx-auto">
            <CardHeader className="text-center pb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4 mx-auto">
                    <Shield className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="flex items-center justify-center space-x-2">
                    <Ticket size={20} />
                    <span>Invitation Required</span>
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-2">
                    Enter your invitation code to create an account and join PlayHub
                </p>
            </CardHeader>

            <CardContent className="space-y-6">
                <div className="space-y-2">
                    <Label htmlFor="invitation-code" className="text-sm font-medium">
                        Invitation Code
                    </Label>
                    <div className="relative">
                        <Ticket
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                            size={16}
                        />
                        <Input
                            id="invitation-code"
                            type="text"
                            placeholder="ABCD-EFGH-1234"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && code.trim() && !loading) {
                                    handleValidate();
                                }
                            }}
                            className="pl-10 text-center tracking-wider font-mono uppercase bg-gray-100"
                            required
                            disabled={loading}
                            maxLength={15}
                        />
                    </div>
                </div>

                <Button
                    onClick={handleValidate}
                    className="w-full"
                    size="lg"
                    disabled={!code.trim() || loading}
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Validating Code...
                        </>
                    ) : (
                        <>
                            <Lock className="w-4 h-4 mr-2" />
                            Validate & Continue
                        </>
                    )}
                </Button>

                <div className="text-center space-y-2">
                    <p className="text-xs text-muted-foreground">
                        Don't have an invitation code?
                    </p>
                    <button
                        className="text-xs text-primary hover:underline focus:outline-none focus:underline transition-colors"
                        onClick={() => toast.info('Contact support for an invitation code')}
                    >
                        Request an invitation
                    </button>
                </div>
            </CardContent>
        </Card>
    );
}